import css from './Ticket.module.scss'
import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch } from 'react-redux'
import MediaQuery from 'react-responsive'

import { ITicket } from 'meta/backendControlled/structure'
import { TicketCartAction, TicketRules } from 'meta/backendControlled/structure/ticket'
import { BreakpointsMaxWidth } from 'meta/layout/breakpoints'
import { PaymentMethod } from 'meta/pages/checkout'
import { CheckoutActions } from 'store/pages/checkout'

import PaymentMethodDialogError from 'components/Checkout/PaymentMethodDialogError'
import Desktop from 'components/Checkout/TicketSelection/Automatic/Ticket/Desktop'
import Mobile from 'components/Checkout/TicketSelection/Automatic/Ticket/Mobile'
import {
  useCheckoutCartLastTypeTicket,
  useCheckoutCartTickets,
  useCheckoutDetails,
  useCheckoutPayment,
} from 'hooks/useCheckout'

interface IProps {
  data: ITicket
  analyticsCallback(typeTicketId: number, removeFromCart?: boolean): void
  isGroupTicket: boolean
}

const MAX_TICKETS_FOR_NONSIAE_EVENTS = 100

const Ticket: React.FC<IProps> = (props) => {
  const { data, analyticsCallback, isGroupTicket } = props

  const { t } = useTranslation()
  const dispatch = useDispatch()
  const checkoutDetails = useCheckoutDetails()
  const cartTickets = useCheckoutCartTickets()
  const lastTypeTicket = useCheckoutCartLastTypeTicket()
  const payment = useCheckoutPayment()
  const [stateVoucherError, setStateVoucherError] = useState<boolean>(false)

  const [lastCartAction, setLastCartAction] = useState<TicketCartAction | null>(null)

  const maxTickets = checkoutDetails?.purchaseQuantityLimit ?? MAX_TICKETS_FOR_NONSIAE_EVENTS

  const checkIfTicketIsAddableAccordingToRules = (data: ITicket, quantity: number): boolean => {
    const cartTotal = cartTickets?.reduce<number>((total, { quantity }) => total + quantity, 0) ?? 0

    const currentValue =
      cartTickets && cartTickets.find((x) => x.typeTicketId == data.id)
        ? (cartTickets.find((x) => x.typeTicketId == data.id)?.quantity ?? 0)
        : 0

    const transactionStep = data.saleRules?.find((x) => x.rule == TicketRules.transactionStep)?.value ?? 1

    const transactionMaxQuantity =
      data.saleRules?.find((x) => x.rule == TicketRules.transactionMaxQuantity)?.value ?? null

    if (maxTickets && cartTotal + quantity > maxTickets) {
      alert(String(t('checkout:max_num_tickets_alert', { maxTickets })))
      return false
    }

    if (transactionMaxQuantity && currentValue && currentValue + transactionStep > transactionMaxQuantity) {
      alert(String(t('checkout:max_num_single_tickets_alert', { maxTickets: transactionMaxQuantity })))
      return false
    }

    return true
  }

  const getTicketVariationQuantity = (data: ITicket, remove?: boolean): number => {
    const currentValue =
      cartTickets && cartTickets.find((x) => x.typeTicketId == data.id)
        ? cartTickets.find((x) => x.typeTicketId == data.id)?.quantity
        : 0

    const transactionMinQuantity =
      data.saleRules?.find((x) => x.rule == TicketRules.transactionMinQuantity)?.value ?? null

    const transactionStep = data.saleRules?.find((x) => x.rule == TicketRules.transactionStep)?.value ?? 1

    if (remove && transactionMinQuantity && currentValue == transactionMinQuantity) {
      return transactionStep > transactionMinQuantity ? transactionStep : transactionMinQuantity
    }

    if (currentValue == 0 && transactionMinQuantity) {
      return transactionStep > transactionMinQuantity ? transactionStep : transactionMinQuantity
    }

    return transactionStep
  }

  const addTickets = () => {
    const cartTotal = cartTickets?.reduce<number>((total, { quantity }) => total + quantity, 0) ?? 0

    const isStateVoucherPMActive =
      payment &&
      payment?.methodCurrent &&
      [PaymentMethod.diciottoApp, PaymentMethod.cartaMeritoCultura].includes(payment.methodCurrent)

    if (isStateVoucherPMActive && cartTotal >= 1) {
      setStateVoucherError(true)
      return
    }

    const quantity = getTicketVariationQuantity(data)

    const canTicketBeAdded = checkIfTicketIsAddableAccordingToRules(data, quantity)

    if (canTicketBeAdded) {
      setLastCartAction(TicketCartAction.add)

      dispatch(CheckoutActions.addToCart({ id: data.id, quantity }))
    }
  }

  const removeTickets = () => {
    const quantity = getTicketVariationQuantity(data, true)

    setLastCartAction(TicketCartAction.remove)

    dispatch(CheckoutActions.removeFromCart({ id: data.id, quantity }))

    analyticsCallback(data.id, true)
  }

  useEffect(() => {
    if (lastTypeTicket === data.id && lastCartAction == TicketCartAction.add) {
      analyticsCallback(data.id)
    }
  }, [cartTickets])

  return (
    <div className={css.checkout_ticketselection_automatic_ticket_container} data-testid={`ticket-${data.id}`}>
      <MediaQuery maxWidth={BreakpointsMaxWidth.mobile}>
        <Mobile data={data} addTickets={addTickets} removeTickets={removeTickets} isGroupTicket={isGroupTicket} />
      </MediaQuery>

      <MediaQuery minWidth={BreakpointsMaxWidth.mobile}>
        <Desktop data={data} addTickets={addTickets} removeTickets={removeTickets} isGroupTicket={isGroupTicket} />
      </MediaQuery>

      {stateVoucherError && (
        <PaymentMethodDialogError
          paymentLabel={
            payment?.methodCurrent === PaymentMethod.diciottoApp
              ? t('checkout:diciotto_app')
              : t('checkout:carta_merito_cultura')
          }
          messageKey={'checkout:error_payment_method_change_payment'}
          onClose={() => setStateVoucherError(false)}
        />
      )}
    </div>
  )
}

export default Ticket
